import { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useI18n } from '../context/I18nContext';
import { shallowCompareObjects } from '../utils/shallowCompare';
import logger from '../utils/logger';

const DURATION = {
  aSecond: 1,
  aMinute: 60,
  aHour: 60 * 60,
  aDay: 24 * 60 * 60,
  aWeek: 7 * 24 * 60 * 60,
  aMonth: 30 * 24 * 60 * 60,
  aYear: 365 * 24 * 60 * 60,
};

const defaultProps = {
  style: {},
  unixTime: null,
  displayTitle: true,
  showNow: false,
  short: false,
  briefDate: false,
  buildTime: false,
  eventTime: false,
  full: false,
};

const FancyTimeStamp = (props) => {
  props = { ...defaultProps, ...props };
  const { unixTime, style, displayTitle, showNow, short, briefDate, buildTime, eventTime, full, currentLanguage, ...otherProps } = props;
  const [unixNow, setUnixNow] = useState(Date.now() / 1000);
  const { t } = useI18n();

  const scheduleUpdate = useCallback(() => {
    if (unixTime === null) return;

    const timer = setTimeout(() => {
      setUnixNow(Date.now() / 1000);
    }, DURATION.aMinute * 1000);

    return () => clearTimeout(timer);
  }, [unixTime]);

  useEffect(() => {
    // sets current locale for moment and return current locale as string e.g. "en"
    moment.locale(currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    if (!showNow) return;
    return scheduleUpdate();
  }, [scheduleUpdate]);

  const getFancyTimeString = useCallback(() => {
    if (unixTime === null) return null;

    if (!parseInt(unixTime) || !parseInt(unixNow)) {
      logger.error('FancyTimeStamp: bad value for times. unixTime=', unixTime, 'unixNow=', unixNow);
    }

    const now = moment(unixNow, 'X');
    const time = moment(unixTime, 'X');
    const timeDiff = now.unix() - time.unix();
    const dayEnd = moment(moment().endOf('day'), 'X');

    switch (true) {
      case full:
        return time.format(t('FancyTimeStamp.fullFormat'));
      case short && now.diff(time, 'minutes') <= 0 && showNow:
        return t('FancyTimeStamp.now');
      case short && now.diff(time, 'minutes') <= 60:
        return t('FancyTimeStamp.xMin', { numMin: now.diff(time, 'minutes') });
      case short && now.diff(time, 'days') < 1:
        return now.date() !== time.date()
          ? t('FancyTimeStamp.yesterday')
          : time.format('LT');
      case short && dayEnd.diff(time, 'days') === 1:
        return t('FancyTimeStamp.yesterday');
      case short && dayEnd.diff(time, 'days') > 1:
        return now.year() !== time.year()
          ? time.format(t('FancyTimeStamp.shortFormatMonthYear'))
          : time.format(t('FancyTimeStamp.shortFormatMonthDay'));
      case eventTime:
        return time.format('lll');
      case briefDate:
        return time.format(t('FancyTimeStamp.briefDate'));
      case buildTime:
        return time.format('llll');
      case timeDiff < 2 * DURATION.aMinute && showNow:
        return t('FancyTimeStamp.now');
      case timeDiff < DURATION.aDay && now.date() === time.date():
        return time.format('LT');
      case timeDiff < DURATION.aWeek:
        return time.format(t('FancyTimeStamp.medFormatWeekdayTime'));
      case timeDiff < 3 * DURATION.aMonth:
        return time.format(t('FancyTimeStamp.medFormatRecentMonthDayTime'));
      default:
        return time.format(t('FancyTimeStamp.medFormatExactDayTime'));
    }    
  }, [unixTime, unixNow, full, short, showNow, eventTime, briefDate, buildTime]);

  const getFullTimeString = useCallback(() => {
    if (unixTime === null) return null;
    return moment.unix(unixTime).format(t('FancyTimeStamp.fullFormatToolTip'));
  }, [unixTime]);

  if (unixTime === null) return null;

  const intTime = parseInt(unixTime);

  if (!(intTime && intTime > 0)) {
    logger.error('Bad value for unixTime=', unixTime);
    return null;
  }

  const titleText = displayTitle ? getFullTimeString() : null;
  const formattedTime = getFancyTimeString();

  return (
    <span style={style} title={titleText} {...otherProps}>
      {formattedTime}
    </span>
  );
};

FancyTimeStamp.propTypes = {
  style: PropTypes.object,
  unixTime: PropTypes.number,
  displayTitle: PropTypes.bool,
  showNow: PropTypes.bool,
  short: PropTypes.bool,
  briefDate: PropTypes.bool,
  buildTime: PropTypes.bool,
  eventTime: PropTypes.bool,
  full: PropTypes.bool,
  currentLanguage: PropTypes.string,
};

const areEqual = (prevProps, nextProps) => (
  prevProps.unixTime === nextProps.unixTime &&
  prevProps.displayTitle === nextProps.displayTitle &&
  prevProps.showNow === nextProps.showNow &&
  prevProps.short === nextProps.short &&
  prevProps.briefDate === nextProps.briefDate &&
  prevProps.buildTime === nextProps.buildTime &&
  prevProps.eventTime === nextProps.eventTime &&
  prevProps.full === nextProps.full &&
  shallowCompareObjects(prevProps.style, nextProps.style)
);

export default memo(FancyTimeStamp, areEqual);
